import { useState, useEffect, useRef, useCallback } from 'react';
import WholesaleLayout, { useWholesaleAuth } from './WholesaleLayout';
import { getWholesaleConversations, getConversationMessages, sendConversationMessage } from '../../lib/api';

export default function WholesaleConversations() {
  const user = useWholesaleAuth('wholesale');
  const isAdmin = user?.role === 'admin';

  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const msgEnd = useRef(null);

  const loadConvos = useCallback(() => {
    if (!user) return;
    getWholesaleConversations().then(r => {
      setConvos(r.data);
      if (!isAdmin && r.data.length > 0 && !active) setActive(r.data[0].wholesaler);
    }).catch(() => {});
  }, [user, isAdmin, active]);

  const loadMessages = useCallback(() => {
    if (!active) return;
    getConversationMessages(active).then(r => setMessages(r.data)).catch(() => {});
  }, [active]);

  useEffect(() => { loadConvos(); const iv = setInterval(loadConvos, 15000); return () => clearInterval(iv); }, [loadConvos]);
  useEffect(() => { loadMessages(); const iv = setInterval(loadMessages, 5000); return () => clearInterval(iv); }, [loadMessages]);
  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // For non-admin with no conversations yet, auto-set active to own username
  useEffect(() => {
    if (user && !isAdmin && !active) setActive(user.username);
  }, [user, isAdmin, active]);

  const handleSend = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      await sendConversationMessage(active, { text: text.trim() });
      setText('');
      loadMessages();
      loadConvos();
    } catch (e) { alert(e.response?.data?.detail || 'Send failed'); }
    setSending(false);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const title = isAdmin ? 'Conversations' : 'Messages';

  return (
    <WholesaleLayout user={user} title={title}>
      <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 120px)', background: '#fff', border: '1px solid #E0DED8', borderRadius: 4, overflow: 'hidden' }}>
        {/* Sidebar - only for admin */}
        {isAdmin && (
          <div style={{ width: 280, borderRight: '1px solid #E0DED8', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E0DED8' }}>
              <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#888' }}>Wholesalers</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {convos.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 12 }}>No conversations yet</div>}
              {convos.map(c => (
                <div key={c.wholesaler} data-testid={`convo-${c.wholesaler}`}
                  onClick={() => setActive(c.wholesaler)}
                  style={{ padding: '14px 20px', borderBottom: '1px solid #F0EEE8', cursor: 'pointer', background: active === c.wholesaler ? '#F7F6F2' : '#fff', transition: 'background 0.1s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{c.company_name || c.wholesaler}</span>
                    {c.unread > 0 && <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#C41E2A', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{c.unread}</span>}
                  </div>
                  {c.last_message && <div style={{ fontSize: 11, color: '#888', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.last_sender}: {c.last_message}</div>}
                  {c.last_at && <div style={{ fontSize: 10, color: '#bbb', marginTop: 2 }}>{new Date(c.last_at).toLocaleDateString()}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {active ? (
            <>
              <div style={{ padding: '14px 24px', borderBottom: '1px solid #E0DED8', fontWeight: 600, fontSize: 14 }}>
                {isAdmin ? (convos.find(c => c.wholesaler === active)?.company_name || active) : 'Vardhman Marketing'}
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {messages.length === 0 && <div style={{ textAlign: 'center', color: '#aaa', fontSize: 12, padding: 40 }}>No messages yet. Start the conversation.</div>}
                {messages.map(m => {
                  const isOwn = m.sender === user?.username;
                  return (
                    <div key={m.id} style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start' }}>
                      <div style={{ maxWidth: '65%', padding: '10px 14px', borderRadius: 8, background: isOwn ? '#111' : '#F0EEE8', color: isOwn ? '#fff' : '#111', fontSize: 13, lineHeight: 1.5 }}>
                        {!isOwn && <div style={{ fontSize: 10, fontWeight: 600, color: isOwn ? '#aaa' : '#888', marginBottom: 4 }}>{m.sender_name}</div>}
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                        <div style={{ fontSize: 9, color: isOwn ? '#777' : '#aaa', marginTop: 4, textAlign: 'right' }}>{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEnd} />
              </div>
              <div style={{ padding: '12px 24px', borderTop: '1px solid #E0DED8', display: 'flex', gap: 10 }}>
                <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type a message..." rows={1}
                  style={{ flex: 1, padding: '10px 14px', border: '1px solid #E0DED8', borderRadius: 4, fontFamily: 'Barlow', fontSize: 13, resize: 'none', outline: 'none', minHeight: 40, maxHeight: 100 }} data-testid="chat-input" />
                <button onClick={handleSend} disabled={sending || !text.trim()} data-testid="chat-send-btn"
                  style={{ padding: '10px 20px', background: text.trim() ? '#C41E2A' : '#ccc', color: '#fff', border: 'none', borderRadius: 4, fontFamily: 'Barlow', fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', cursor: text.trim() ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
                  Send
                </button>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#aaa', fontSize: 13 }}>
              Select a conversation to start messaging
            </div>
          )}
        </div>
      </div>
    </WholesaleLayout>
  );
}
